import { Injectable } from '@tanbo/vue-di-plugin'
import { TableList, TableMemoryPagination } from '@/services/table-memory-pagination'
import { Http } from '@/services/http'
import { InvokeThrottle } from '@/common/decorators/invoke-throttle'
import { ParamsClean } from '@/common/decorators/params-clean'
import { HandleType, RepairTreeItem } from '@/views/dashboard/safe-status/base-line/repair-item-tree-type'
import { DUBA_BASELINE_REPAIR_ITEMS_KEY } from '@/views/dashboard/safe-status/base-line/baseline-repair-items-cache'
import { AuthStatusList } from '@/views/dashboard/_type/client-item'
import { getAreaByTerminalType } from '@/services/service-permission.service'

/* eslint-disable no-magic-numbers */
export enum CheckResult {
  pass=1,
  unPass=2,
  all=3
}

export interface RepairParams {
  /** 选择类型,1-指定终端，2-指定分组 */
  select_type: number
  /** 终端设备id列表 */
  device_list: string[]
  /** 分组id */
  group_id: number
  // 指定自动修复项
  repair_item_ids: string[],
  // 仅通知规则id列表
  notice_item_ids: string[]

}

export interface RepairTerminalItem {
  /** 终端ID */
  device_id: string
  /** 计算机名 */
  computer_name: string
  /** 所属分组 */
  group_name: string
  /** 登记用户名 */
  user_mark: string
  /** 外网ip地址 */
  client_ip: string
  /** 内网ip地址 */
  system_ip: string
  /** 操作系统 */
  system_name: string
  /** 终端状态 1：在线，2：离线 */
  device_status: number
  /** 系统类型 1-windows 2-linux */
  system_type?: number
  /** 扫描时间 */
  last_check_time: number
  /** 不合规数量 */
  substandard_num: number
  /** 授权状态集合 */
  auth_status?: AuthStatusList[]
  /** 前端字段 */
  area_type?: number
}

export interface QueryParams {
  /** 分组id */
  group_id: number
  /** 页码 */
  page: number
  /** 每页数量 */
  limit: number
  /** 排序 */
  asc?: boolean
  /** 排序字段 */
  sort?: string
  /** 是否使用内存数据 */
  useMemory: boolean
  /** 基线检查结果，1为合规，2为不合规，3为全部 */
  check_result: number
  permission_names?: string[]
  key_word?: string
}
interface SystemIDs{
  autoIDs: string[],
  notifyIDs: string[],
  ignoreIDs: string[]
}
export interface IdsCount{
  alen: number,
  nlen: number,
  ilen: number,
  total: number,
  autoIDs: string[],
  notifyIDs: string[]
}
export interface DevicesSubStandardNumListReq {
  // 选择类型，1-终端列表,2-当前分组全部终端
  select_type: number,
  // 终端列表, 当select_type=1时不可为空
  device_list: string[],
  // 分组id
  group_id: number
}
@Injectable()
export class BaseLineRepairDrawerService extends TableMemoryPagination<QueryParams, RepairTerminalItem> {
  selectedWinIDs: SystemIDs
  selectedLinuxIDs: SystemIDs
  allAutoIDs: string[]
  allNotifyIDs: string[]
  allIgnoreIDs: string[]
  constructor (private http: Http) {
    super()
    this.selectedWinIDs = this.selectedLinuxIDs = {
      autoIDs: [],
      notifyIDs: [],
      ignoreIDs: []
    }
    this.initBaseData()
  }

  initBaseData () {
    const raw = localStorage.getItem(DUBA_BASELINE_REPAIR_ITEMS_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    const winBaseLineItems: RepairTreeItem[] = parsed?.category || []
    const linuxBaseLineItems: RepairTreeItem[] = parsed?.linux_category || []
    this.allAutoIDs = this.getAutoItemsId(winBaseLineItems)
    this.allNotifyIDs = [...this.getNotifyItemsId(winBaseLineItems), ...this.getNotifyItemsId(linuxBaseLineItems)]
    this.allIgnoreIDs = this.getIgnoreItemsId(winBaseLineItems)
  }

  isEqual (left: RepairTerminalItem, right: RepairTerminalItem): boolean {
    return left.device_id === right.device_id
  }

  @ParamsClean()
  getListByServer (params: QueryParams): Promise<TableList<RepairTerminalItem>> {
    return this.http.post<{
      list: RepairTerminalItem[]
      enable_total: number
      total: number
    }>('/baselinecheck/terminals/check-notify/get', params).then(res => {
      res.list.forEach(v => v.area_type = getAreaByTerminalType(v.system_type))
      return res
    })
  }

  // eslint-disable-next-line no-magic-numbers
  @InvokeThrottle(800)
  getSubStandardIds (params: DevicesSubStandardNumListReq) {
    return this.http.post<{
      /** 基线检查所有检查项json数据 */
      substandard_ids : string[]
    }>('/baseline-check/device-substandard/get', params)
  }

  isInCollection (type: HandleType, id: string): boolean {
    if (type === HandleType.AUTO) {
      return this.allAutoIDs.includes(id)
    } else if (type === HandleType.NOTIFY) {
      return this.allNotifyIDs.includes(id)
    }
    return this.allIgnoreIDs.includes(id)
  }

  /**
   * @name getAutoItemsId 获取所有自动修复项的ID
   * @param category 扫描项集合
   */
  getAutoItemsId (category: RepairTreeItem[]) : string[] {
    return category.map((item:RepairTreeItem) => {
      if (item.scan_level !== undefined) {
        return item.handle_type === HandleType.AUTO ? item.id : null
      }
      return this.getAutoItemsId(item.items)
      // eslint-disable-next-line no-magic-numbers
    }).filter(item => !!item).flat(2)
  }

  /**
   * @name getNotifyItemsId 获取所有仅通知项的ID
   * @param category 扫描项集合
   */
  getNotifyItemsId (category: RepairTreeItem[]) : string[] {
    return category.map((item:RepairTreeItem) => {
      if (item.scan_level !== undefined) {
        return item.handle_type === HandleType.NOTIFY ? item.id : null
      }
      return this.getNotifyItemsId(item.items)
      // eslint-disable-next-line no-magic-numbers
    }).filter(item => !!item).flat(2)
  }

  /**
   * @name getIgnoreItemsId 获取所有不处理修复项的ID
   * @param category 扫描项集合
   */
  getIgnoreItemsId (category: RepairTreeItem[]) : string[] {
    return category.map((item:RepairTreeItem) => {
      if (item.scan_level !== undefined) {
        return item.handle_type === HandleType.IGNORE ? item.id : null
      }
      return this.getIgnoreItemsId(item.items)
      // eslint-disable-next-line no-magic-numbers
    }).filter(item => !!item).flat(2)
  }

  getRepairNotification (data: RepairParams) {
    return this.http.post<{ task_id: string }>('/baselinecheck/repair-notice', data)
  }

  filterSubstandardIds (handleType: HandleType, subIds: string[]) {
    if (handleType === HandleType.AUTO) {
      return subIds.filter(id => this.allAutoIDs.includes(id))
    } else if (handleType === HandleType.NOTIFY) {
      return subIds.filter((id:string) => this.allNotifyIDs.includes(id))
    }
    return subIds.filter((id: string) => this.allIgnoreIDs.includes(id))
  }

  generateIDs (subIds: string[]): IdsCount {
    const allSelectedAutoIDs: string[] = []
    const allSelectedNotifyIDs: string[] = []
    const allSelectedIgnoreIDs: string[] = []
    if (!this.selectedWinIDs && !this.selectedLinuxIDs) {
      const autoIDs = this.filterSubstandardIds(HandleType.AUTO, subIds)
      const notifyIDs = this.filterSubstandardIds(HandleType.NOTIFY, subIds)
      return {
        alen: autoIDs.length,
        nlen: notifyIDs.length,
        ilen: this.filterSubstandardIds(HandleType.IGNORE, subIds).length,
        total: subIds.length,
        autoIDs,
        notifyIDs
      }
    }
    const allSystemSelectedIds = {
      auto: this.selectedWinIDs.autoIDs,
      notify: [...this.selectedWinIDs.notifyIDs, ...this.selectedLinuxIDs.notifyIDs],
      ignore: [...this.selectedWinIDs.ignoreIDs, ...this.selectedLinuxIDs.ignoreIDs]
    }
    const auto2NotifyIds = this.filterSubstandardIds(HandleType.AUTO, subIds).filter((id:string) => allSystemSelectedIds.notify.includes(id))
    const auto2IgnoreIds = this.filterSubstandardIds(HandleType.AUTO, subIds).filter((id:string) => allSystemSelectedIds.ignore.includes(id))
    const notify2IgnoreIds = this.filterSubstandardIds(HandleType.NOTIFY, subIds).filter((id:string) => allSystemSelectedIds.ignore.includes(id))
    const autoIDs = this.filterSubstandardIds(HandleType.AUTO, subIds).filter((id:string) => ![...allSystemSelectedIds.notify, ...allSystemSelectedIds.ignore].includes(id))
    const notifyIDs = [...this.filterSubstandardIds(HandleType.NOTIFY, subIds).filter((id:string) => !allSystemSelectedIds.ignore.includes(id)), ...auto2NotifyIds]
    const ignoreIDs = [...this.filterSubstandardIds(HandleType.IGNORE, subIds), ...auto2IgnoreIds, ...notify2IgnoreIds]
    return {
      alen: autoIDs.length,
      nlen: notifyIDs.length,
      ilen: ignoreIDs.length,
      total: autoIDs.length + notifyIDs.length + ignoreIDs.length,
      autoIDs,
      notifyIDs
    }
  }
}
